/**
 * First-party product analytics (no third-party tracker).
 *
 *   track('search', { league: 'nba' })   // queue an event
 *   <AnalyticsTracker />                  // App.js: page views + heartbeat + flush
 *
 * Events are batched and sent to the `trackActivity` callable every few
 * seconds, when the tab hides, and as a heartbeat every 5 minutes while the
 * tab is visible (that's what "last active" and sessions are built from).
 * A random anonymous id (localStorage) links pre-signup visits to the funnel;
 * first-touch attribution (utm_*, referrer, landing page) is captured once.
 * Nothing here ever throws into the app.
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const ANON_KEY = 'bo_anon_id';
const ATTR_KEY = 'bo_first_touch';
const FLUSH_MS = 8000;
const HEARTBEAT_MS = 5 * 60 * 1000;
const MAX_QUEUE = 100;

let queue = [];
let timer = null;
let attributionSent = false;

const store = {
    get: (k) => { try { return typeof localStorage !== 'undefined' ? localStorage.getItem(k) : null; } catch { return null; } },
    set: (k, v) => { try { if (typeof localStorage !== 'undefined') localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export function anonId() {
    let id = store.get(ANON_KEY);
    if (!id) {
        id = `a_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
        store.set(ANON_KEY, id);
    }
    return id;
}

/** First-touch attribution, captured on the first page load ever. */
export function firstTouch() {
    const saved = store.get(ATTR_KEY);
    if (saved) { try { return JSON.parse(saved); } catch { /* re-capture */ } }
    if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const attr = { landing: window.location.pathname.slice(0, 300), firstSeenAt: new Date().toISOString() };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => { const v = params.get(k); if (v) attr[k] = v.slice(0, 120); });
    try {
        const ref = document.referrer ? new URL(document.referrer) : null;
        if (ref && ref.host !== window.location.host) attr.referrer = `${ref.origin}${ref.pathname}`.slice(0, 300);
    } catch { /* no referrer */ }
    store.set(ATTR_KEY, JSON.stringify(attr));
    return attr;
}

const currentPath = () => (Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.pathname : null);

export function track(name, props) {
    try {
        queue.push({ name, props: props || null, ts: Date.now(), path: currentPath() });
        if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
        schedule();
    } catch { /* never break the app */ }
}

function schedule() {
    if (timer) return;
    timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

export async function flush({ heartbeat = false } = {}) {
    if (!queue.length && !heartbeat) return;
    const events = queue.splice(0, 25);
    if (heartbeat && !events.length) events.push({ name: 'heartbeat', ts: Date.now(), path: currentPath() });
    const payload = { anonId: anonId(), events, platform: Platform.OS === 'web' ? 'web' : Platform.OS };
    if (!attributionSent) payload.attribution = firstTouch();
    try {
        await httpsCallable(getFunctions(getApp()), 'trackActivity')(payload);
        attributionSent = true;
    } catch {
        // Offline / function missing: drop silently (analytics must never block).
    }
    if (queue.length) schedule();
}

/** Mount once (App.js). Tracks page views on screen change + heartbeats. */
export function AnalyticsTracker({ screen, uid }) {
    const lastScreen = useRef(null);
    const lastUid = useRef(uid);

    useEffect(() => { firstTouch(); }, []);

    useEffect(() => {
        if (!screen || screen === lastScreen.current) return;
        lastScreen.current = screen;
        track('page_view', { screen });
    }, [screen]);

    useEffect(() => {
        // Signing in re-attributes the next batch (and its heartbeat) to the user.
        if (uid && uid !== lastUid.current) { attributionSent = false; flush({ heartbeat: true }); }
        lastUid.current = uid;
    }, [uid]);

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
        const beat = setInterval(() => { if (document.visibilityState === 'visible') flush({ heartbeat: true }); }, HEARTBEAT_MS);
        const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
        document.addEventListener('visibilitychange', onHide);
        return () => { clearInterval(beat); document.removeEventListener('visibilitychange', onHide); };
    }, []);

    return null;
}

// Test hooks
export const _test = { reset: () => { queue = []; if (timer) clearTimeout(timer); timer = null; attributionSent = false; }, queue: () => queue };
